import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2, CheckCircle2, XCircle, DollarSign, Tag, Type, Image as ImageIcon } from 'lucide-react';
import { authHelper } from '../utils/authHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminLandingEditor() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [content, setContent] = useState({
    hero: {
      title: '',
      subtitle: '',
      cta: '',
      creator_image: ''
    },
    pricing: {
      app_mobile: {
        name: '',
        price: 0,
        currency: '$',
        period: 'mes',
        trial_days: 7,
        cta: ''
      },
      web_platform: {
        name: '',
        price: 0,
        currency: '$',
        period: 'único',
        description: ''
      }
    }
  });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/content`);
      if (response.ok) {
        const data = await response.json();
        setContent({
          hero: data.hero,
          pricing: data.pricing
        });
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setMessage({ type: 'error', text: 'Error al cargar contenido' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const token = authHelper.getToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/landing/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: '¡Contenido actualizado exitosamente!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar');
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updatePricingField = (package_id, field, value) => {
    setContent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [package_id]: {
          ...prev.pricing[package_id],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A14C]" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Editor de Landing Page</h2>
          <p className="text-sm text-white/60">Modifica el contenido del landing page</p>
        </div>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#C5A14C] text-[#02040A] font-bold rounded-full px-6 py-3 hover:bg-[#B08F3C] transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Guardando...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              Guardar Cambios
            </>
          )}
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' 
              ? 'bg-emerald-500/10 border border-emerald-500/20' 
              : 'bg-red-500/10 border border-red-500/20'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
          ) : (
            <XCircle className="w-5 h-5 text-red-500" />
          )}
          <p className={message.type === 'success' ? 'text-emerald-400' : 'text-red-400'}>
            {message.text}
          </p>
        </motion.div>
      )}

      {/* Hero Section */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <Type className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-xl font-bold text-white">Sección Hero</h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">Título Principal</label>
            <input
              type="text"
              value={content.hero.title}
              onChange={(e) => updateField('hero', 'title', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
              placeholder="Gestión Profesional de Trading"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Subtítulo</label>
            <input
              type="text"
              value={content.hero.subtitle}
              onChange={(e) => updateField('hero', 'subtitle', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
              placeholder="Controla tu disciplina, protege tu capital"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Texto del CTA</label>
            <input
              type="text"
              value={content.hero.cta}
              onChange={(e) => updateField('hero', 'cta', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
              placeholder="Comenzar Prueba Gratis"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                URL de Imagen del Creador
              </div>
            </label>
            
            {/* Upload Button */}
            <div className="mb-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    const formData = new FormData();
                    formData.append('file', file);
                    
                    try {
                      const response = await fetch(`${API_URL}/api/uploads/image`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${authHelper.getToken()}`
                        },
                        body: formData
                      });
                      
                      if (response.ok) {
                        const data = await response.json();
                        const fullUrl = `${window.location.origin}${data.url}`;
                        updateField('hero', 'creator_image', fullUrl);
                        setMessage({ type: 'success', text: 'Imagen subida correctamente' });
                        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                      } else {
                        setMessage({ type: 'error', text: 'Error al subir imagen' });
                      }
                    } catch (err) {
                      setMessage({ type: 'error', text: 'Error de conexión' });
                    }
                  }}
                />
                <div className="bg-[#C5A14C]/20 hover:bg-[#C5A14C]/30 border border-[#C5A14C]/40 rounded-lg px-4 py-3 text-center transition-colors">
                  <span className="text-[#C5A14C] font-semibold">📤 Subir Imagen Desde Computadora</span>
                </div>
              </label>
              <p className="text-xs text-white/40 mt-1">
                O pega una URL externa abajo
              </p>
            </div>
            
            <input
              type="url"
              value={content.hero.creator_image || ''}
              onChange={(e) => updateField('hero', 'creator_image', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
              placeholder="https://ejemplo.com/imagen-creador.png"
            />
            <p className="text-xs text-white/40 mt-2">
              💡 Tip: Usa <a href="https://remove.bg" target="_blank" rel="noopener noreferrer" className="text-[#C5A14C] hover:underline">Remove.bg</a> para remover el fondo de tu imagen
            </p>
            {content.hero.creator_image && (
              <div className="mt-4">
                <p className="text-xs text-white/60 mb-2">Vista Previa:</p>
                <div className="relative w-48 h-48 rounded-lg overflow-hidden border border-white/20">
                  <img 
                    src={content.hero.creator_image} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="absolute inset-0 bg-red-500/10 border border-red-500/20 rounded-lg hidden items-center justify-center">
                    <p className="text-xs text-red-400">❌ Error al cargar imagen</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pricing - App Móvil */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <DollarSign className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-xl font-bold text-white">Pricing - App Móvil</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">Nombre</label>
            <input
              type="text"
              value={content.pricing.app_mobile.name}
              onChange={(e) => updatePricingField('app_mobile', 'name', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Precio (USD)</label>
            <input
              type="number"
              step="0.01"
              value={content.pricing.app_mobile.price}
              onChange={(e) => updatePricingField('app_mobile', 'price', parseFloat(e.target.value))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Período</label>
            <select
              value={content.pricing.app_mobile.period}
              onChange={(e) => updatePricingField('app_mobile', 'period', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            >
              <option value="mes">mes</option>
              <option value="año">año</option>
            </select>
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Días de Prueba Gratis</label>
            <input
              type="number"
              value={content.pricing.app_mobile.trial_days}
              onChange={(e) => updatePricingField('app_mobile', 'trial_days', parseInt(e.target.value))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-white/60 mb-2">Texto del CTA</label>
            <input
              type="text"
              value={content.pricing.app_mobile.cta}
              onChange={(e) => updatePricingField('app_mobile', 'cta', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
          </div>
        </div>
      </div>

      {/* Pricing - Plataforma Web */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <div className="flex items-center gap-2 mb-6">
          <Tag className="w-5 h-5 text-[#C5A14C]" />
          <h3 className="text-xl font-bold text-white">Pricing - Plataforma Web</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-white/60 mb-2">Nombre</label>
            <input
              type="text"
              value={content.pricing.web_platform.name}
              onChange={(e) => updatePricingField('web_platform', 'name', e.target.value)}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-2">Precio (USD)</label>
            <input
              type="number"
              step="0.01"
              value={content.pricing.web_platform.price}
              onChange={(e) => updatePricingField('web_platform', 'price', parseFloat(e.target.value))}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#C5A14C]"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm text-white/60 mb-2">Descripción</label>
            <textarea
              value={content.pricing.web_platform.description}
              onChange={(e) => updatePricingField('web_platform', 'description', e.target.value)}
              rows={3}
              className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#C5A14C]"
              placeholder="Plataforma educativa completa..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
